import { PettyWorkStatus } from "features/PettyWorks/constants/petty-work.constants";

function normalizeStatus(status: string | null | undefined): string {
  return String(status ?? "")
    .trim()
    .toUpperCase()
    .replace(/-/g, "_");
}

export function isTerminalPettyWorkForAssignment(
  status: string | null | undefined,
): boolean {
  const st = normalizeStatus(status);
  return (
    st === PettyWorkStatus.COMPLETED ||
    st === PettyWorkStatus.CANCELLED
  );
}

export function canReassignPettyWork(
  status: string | null | undefined,
): boolean {
  return !isTerminalPettyWorkForAssignment(status);
}

export function shouldShowPettyWorkAssignmentActions(
  status: string | null | undefined,
): boolean {
  return !isTerminalPettyWorkForAssignment(status);
}

/** No new material requests once the petty work is completed, cancelled, or rejected. */
export function pettyWorkAllowsMaterialRequest(
  status: string | null | undefined,
): boolean {
  return !isTerminalPettyWorkForAssignment(status);
}

/**
 * Header fields (title, priority, source channel, description, client,
 * schedule) stay editable until the petty work is completed or cancelled.
 * Once COMPLETED, only a Super Admin may still edit them. Agreed amount and
 * line items are governed separately — see
 * {@link pettyWorkAllowsAgreedAmountUpdate} and {@link pettyWorkAllowsLineItemUpdate}.
 */
export function pettyWorkAllowsHeaderFieldUpdate(
  status: string | null | undefined,
  isSuperAdmin = false,
): boolean {
  const st = normalizeStatus(status);
  if (st === PettyWorkStatus.COMPLETED) return isSuperAdmin;
  return !isTerminalPettyWorkForAssignment(status);
}

/**
 * Agreed amount is editable only while the job hasn't reached billing —
 * unlike header fields and line items, this is never re-opened for a
 * Super Admin post-completion; that path goes through Regenerate Invoice
 * instead, which keeps the invoice in sync.
 */
export function pettyWorkAllowsAgreedAmountUpdate(
  status: string | null | undefined,
): boolean {
  const st = normalizeStatus(status);
  return (
    st === PettyWorkStatus.NEW ||
    st === PettyWorkStatus.IN_PROGRESS ||
    st === PettyWorkStatus.ON_HOLD
  );
}

/**
 * Line items (BOQ) stay editable through every status except CANCELLED —
 * but once COMPLETED, only a Super Admin may still edit them (everyone else
 * is locked out, even though line items would otherwise remain correctable
 * post-completion to support regenerating the invoice).
 */
export function pettyWorkAllowsLineItemUpdate(
  status: string | null | undefined,
  isSuperAdmin = false,
): boolean {
  const st = normalizeStatus(status);
  if (st === PettyWorkStatus.CANCELLED) return false;
  if (st === PettyWorkStatus.COMPLETED) return isSuperAdmin;
  return true;
}

/**
 * Whether the edit page should be reachable at all. Permissive: true whenever
 * either header fields or line items can still be changed (line items remain
 * editable through COMPLETED for a Super Admin, so the page must stay
 * reachable then too, but only for them).
 */
export function canEditPettyWorkStatus(
  status: string | null | undefined,
  isSuperAdmin = false,
): boolean {
  return (
    pettyWorkAllowsHeaderFieldUpdate(status, isSuperAdmin) ||
    pettyWorkAllowsLineItemUpdate(status, isSuperAdmin)
  );
}
